import {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { autoUpdate, offset, size, useFloating } from '@floating-ui/react-dom'

import { FocusRing } from '../components/FocusRing'
import { RefOrEl, normalizeToEl } from '../utilities/normalize-to-el'

// --- Types ---

export type NavigationTargets = {
  nextRef: () => HTMLElement | null
  prevRef: () => HTMLElement | null
  upRef: () => HTMLElement | null
  downRef: () => HTMLElement | null
  leftRef: () => HTMLElement | null
  rightRef: () => HTMLElement | null
  firstRef: () => HTMLElement | null
  lastRef: () => HTMLElement | null
  pageUpRef: () => HTMLElement | null
  pageDownRef: () => HTMLElement | null
}

export type NavigationHandler = (
  focused: HTMLElement,
  targets: NavigationTargets,
  setFocus: (element: HTMLElement) => void,
) => void

export type NavigationHandlers = {
  up?: NavigationHandler
  down?: NavigationHandler
  left?: NavigationHandler
  right?: NavigationHandler
  tab?: NavigationHandler
  shiftTab?: NavigationHandler
  home?: NavigationHandler
  end?: NavigationHandler
  pageUp?: NavigationHandler
  pageDown?: NavigationHandler
  select?: NavigationHandler
}

export type NavigationMode = 'group' | 'menu' | 'grid' | NavigationHandlers

export type LayerOptions = {
  name?: string
  navigation?: NavigationMode
  onSelect?: (element: HTMLElement) => void
  onEnter?: (element: HTMLElement) => void
  onEscape?: () => void
}

export type RefOptions = {
  ref?: RefObject<HTMLElement | null>
  focusTarget?: RefOrEl<HTMLElement>
  focusable?: boolean
  disabled?: boolean
  autoFocus?: boolean
  focusRing?: boolean | ReactElement
  onSelect?: () => void
  ordering?: string
}

export type FocusLayer = readonly [provider: FC<{ children: ReactNode }>, api: FocusAPI]

export type FocusAPI = {
  ref: (options?: RefOptions | RefObject<HTMLElement | null>) => (el: HTMLElement | null) => void
  layer: (active: boolean, options?: LayerOptions) => FocusLayer
  ordering: (keys: string[]) => void
  dispose: () => void
}

type ElementEntry = {
  element: HTMLElement
  target?: HTMLElement
  disabled: boolean
  autoFocus: boolean
  focusRing: boolean | ReactElement
  onSelect?: () => void
  ordering?: string
}

type Layer = {
  id: string
  handlers: NavigationHandlers
  roving: boolean
  active: boolean
  autoFocused: boolean
  onSelect?: (element: HTMLElement) => void
  onEnter?: (element: HTMLElement) => void
  onEscape?: () => void
  savedFocus: HTMLElement | null
  parentId: string | null
  ordering: string[] | null
}

type LayerSlot = {
  provider: FC<{ children: ReactNode }>
  layerId: string
  subSlots: LayerSlot[]
}

type FocusContextValue = { namespace: string; layerId: string }

// --- Store ---

type FocusStore = {
  stack: string[]
  layers: Map<string, Layer>
  elements: Map<string, Map<HTMLElement, ElementEntry>>
}

const stores: Record<string, FocusStore> = {}

const getStore = (namespace: string): FocusStore =>
  (stores[namespace] ??= {
    stack: ['__root__'],
    layers: new Map([
      [
        '__root__',
        {
          id: '__root__',
          handlers: {},
          roving: false,
          active: true,
          autoFocused: false,
          savedFocus: null,
          parentId: null,
          ordering: null,
        },
      ],
    ]),
    elements: new Map(),
  })

// When true, focus was last driven by keyboard interaction.
let isKeyboardInteraction = false

// --- Navigation ---

const moveTo =
  (key: keyof NavigationTargets): NavigationHandler =>
  (_focused, targets, setFocus) => {
    const target = targets[key]()
    if (target) setFocus(target)
  }

const defaultHandlers: NavigationHandlers = {
  tab: moveTo('nextRef'),
  shiftTab: moveTo('prevRef'),
  home: moveTo('firstRef'),
  end: moveTo('lastRef'),
}

const arrowHandlers: NavigationHandlers = {
  up: moveTo('upRef'),
  down: moveTo('downRef'),
  left: moveTo('leftRef'),
  right: moveTo('rightRef'),
  home: moveTo('firstRef'),
  end: moveTo('lastRef'),
}

const menuHandlers: NavigationHandlers = {
  ...defaultHandlers,
  ...arrowHandlers,
}

const gridHandlers: NavigationHandlers = {
  ...arrowHandlers,
  pageUp: moveTo('pageUpRef'),
  pageDown: moveTo('pageDownRef'),
}

const resolveNavigation = (
  mode: NavigationMode | undefined,
): { handlers: NavigationHandlers; roving: boolean } => {
  if (!mode) return { handlers: defaultHandlers, roving: false }
  if (typeof mode === 'object') return { handlers: mode, roving: true }
  switch (mode) {
    case 'group':
      return { handlers: arrowHandlers, roving: false }
    case 'menu':
      return { handlers: menuHandlers, roving: true }
    case 'grid':
      return { handlers: gridHandlers, roving: true }
  }
}

const sortByDomOrder = (entries: ElementEntry[]) =>
  [...entries].sort((a, b) => {
    const pos = a.element.compareDocumentPosition(b.element)
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
    return 0
  })

const sortByOrdering = (entries: ElementEntry[], keys: string[]) => {
  const keyIndex = new Map(keys.map((k, i) => [k, i]))
  return [...entries].sort((a, b) => {
    const aIdx = a.ordering !== undefined ? keyIndex.get(a.ordering) : undefined
    const bIdx = b.ordering !== undefined ? keyIndex.get(b.ordering) : undefined
    if (aIdx !== undefined && bIdx !== undefined) {
      if (aIdx !== bIdx) return aIdx - bIdx
    }
    if (aIdx !== undefined && bIdx === undefined) return -1
    if (aIdx === undefined && bIdx !== undefined) return 1
    const pos = a.element.compareDocumentPosition(b.element)
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
    return 0
  })
}

const getCenter = (rect: DOMRect) => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
})

const tabTarget = (entry: ElementEntry) => entry.target ?? entry.element

const matchesFocused = (entry: ElementEntry, focused: HTMLElement) =>
  entry.element === focused || entry.target === focused

const findSpatialTarget = (
  current: HTMLElement,
  entries: ElementEntry[],
  direction: 'up' | 'down' | 'left' | 'right',
): HTMLElement | null => {
  const currentRect = current.getBoundingClientRect()
  const currentCenter = getCenter(currentRect)

  let bestEntry: ElementEntry | null = null
  let bestScore = Infinity

  for (const entry of entries) {
    if (matchesFocused(entry, current)) continue

    const rect = entry.element.getBoundingClientRect()
    const center = getCenter(rect)

    let isCandidate = false
    switch (direction) {
      case 'right':
        isCandidate = center.x > currentCenter.x
        break
      case 'left':
        isCandidate = center.x < currentCenter.x
        break
      case 'down':
        isCandidate = center.y > currentCenter.y
        break
      case 'up':
        isCandidate = center.y < currentCenter.y
        break
    }

    if (!isCandidate) continue

    const primaryDistance =
      direction === 'left' || direction === 'right'
        ? Math.abs(center.x - currentCenter.x)
        : Math.abs(center.y - currentCenter.y)

    const secondaryDistance =
      direction === 'left' || direction === 'right'
        ? Math.abs(center.y - currentCenter.y)
        : Math.abs(center.x - currentCenter.x)

    const score = primaryDistance + secondaryDistance * 0.3

    if (score < bestScore) {
      bestScore = score
      bestEntry = entry
    }
  }

  return bestEntry ? tabTarget(bestEntry) : null
}

const findPageTarget = (
  current: HTMLElement,
  entries: ElementEntry[],
  direction: 'up' | 'down',
): HTMLElement | null => {
  const viewportHeight = window.innerHeight
  const currentRect = current.getBoundingClientRect()
  const currentCenter = getCenter(currentRect)

  const targetY =
    direction === 'down' ? currentCenter.y + viewportHeight : currentCenter.y - viewportHeight

  let bestEntry: ElementEntry | null = null
  let bestDistance = Infinity

  for (const entry of entries) {
    if (matchesFocused(entry, current)) continue

    const rect = entry.element.getBoundingClientRect()
    const center = getCenter(rect)

    const isCorrectDirection =
      direction === 'down' ? center.y > currentCenter.y : center.y < currentCenter.y

    if (!isCorrectDirection) continue

    const distance = Math.abs(center.y - targetY) + Math.abs(center.x - currentCenter.x) * 0.3

    if (distance < bestDistance) {
      bestDistance = distance
      bestEntry = entry
    }
  }

  return bestEntry ? tabTarget(bestEntry) : null
}

const buildTargets = (
  focused: HTMLElement,
  entries: ElementEntry[],
  ordering: string[] | null,
): NavigationTargets => {
  let sortedCache: ElementEntry[] | null = null
  const sorted = () => {
    if (!sortedCache) {
      sortedCache = ordering ? sortByOrdering(entries, ordering) : sortByDomOrder(entries)
    }
    return sortedCache
  }

  return {
    nextRef: () => {
      const s = sorted()
      if (s.length <= 1) return null
      const i = s.findIndex((e) => matchesFocused(e, focused))
      return tabTarget(s[i >= s.length - 1 ? 0 : i + 1]!) ?? null
    },
    prevRef: () => {
      const s = sorted()
      if (s.length <= 1) return null
      const i = s.findIndex((e) => matchesFocused(e, focused))
      return tabTarget(s[i <= 0 ? s.length - 1 : i - 1]!) ?? null
    },
    upRef: () => findSpatialTarget(focused, entries, 'up'),
    downRef: () => findSpatialTarget(focused, entries, 'down'),
    leftRef: () => findSpatialTarget(focused, entries, 'left'),
    rightRef: () => findSpatialTarget(focused, entries, 'right'),
    firstRef: () => {
      const first = sorted()[0]
      return first ? tabTarget(first) : null
    },
    lastRef: () => {
      const s = sorted()
      const last = s[s.length - 1]
      return last ? tabTarget(last) : null
    },
    pageUpRef: () => findPageTarget(focused, entries, 'up'),
    pageDownRef: () => findPageTarget(focused, entries, 'down'),
  }
}

// --- Helpers ---

const focusElement = (element: HTMLElement) => {
  element.focus()
  element.scrollIntoView({ block: 'nearest' })
}

const getLayerElements = (store: FocusStore, layerId: string) => {
  let map = store.elements.get(layerId)
  if (!map) {
    map = new Map()
    store.elements.set(layerId, map)
  }
  return map
}

const getElementsForLayer = (store: FocusStore, layerId: string): ElementEntry[] => {
  const elements = store.elements.get(layerId)
  if (!elements) return []
  return [...elements.values()].filter((e) => !e.disabled)
}

const getTopmostActiveLayer = (store: FocusStore): { layerId: string; layer: Layer } | null => {
  const layerId = store.stack[store.stack.length - 1]
  if (!layerId) return null
  const layer = store.layers.get(layerId)
  if (!layer) return null
  return { layerId, layer }
}

const findLayerForElement = (
  store: FocusStore,
  element: HTMLElement,
): { layerId: string; layer: Layer } | null => {
  let current: HTMLElement | null = element
  while (current) {
    for (let i = store.stack.length - 1; i >= 0; i -= 1) {
      const layerId = store.stack[i]!
      const layer = store.layers.get(layerId)
      if (!layer) continue
      const entries = getElementsForLayer(store, layerId)
      if (entries.some((e) => e.element === current)) return { layerId, layer }
    }
    current = current.parentElement
  }
  return null
}

const findStoreForElement = (element: HTMLElement | null): FocusStore | null => {
  if (!element) return null
  let current: HTMLElement | null = element
  while (current) {
    for (const store of Object.values(stores)) {
      for (const [layerId, elements] of store.elements) {
        if (!store.stack.includes(layerId)) continue
        if (elements.has(current)) return store
      }
    }
    current = current.parentElement
  }
  return null
}

const findEntryForElement = (element: HTMLElement): ElementEntry | null => {
  let current: HTMLElement | null = element
  while (current) {
    for (const store of Object.values(stores)) {
      for (const [layerId, elements] of store.elements) {
        if (!store.stack.includes(layerId)) continue
        const entry = elements.get(current)
        if (entry) return entry
      }
    }
    current = current.parentElement
  }
  return null
}

const updateTabIndexes = (store: FocusStore, layerId: string) => {
  const layer = store.layers.get(layerId)
  if (!layer) return
  const entries = getElementsForLayer(store, layerId)
  const isTopmost = store.stack[store.stack.length - 1] === layerId
  if (!isTopmost) {
    for (const entry of entries) tabTarget(entry).tabIndex = -1
    return
  }
  if (!layer.roving) {
    for (const entry of entries) tabTarget(entry).tabIndex = 0
    return
  }
  const focused = document.activeElement
  let hasFocused = false
  for (const entry of entries) {
    const el = tabTarget(entry)
    if (el === focused || entry.element === focused) {
      el.tabIndex = 0
      hasFocused = true
    } else {
      el.tabIndex = -1
    }
  }
  if (!hasFocused && entries.length > 0) {
    const ordered = layer.ordering ? sortByOrdering(entries, layer.ordering) : entries
    tabTarget(ordered[0]!).tabIndex = 0
  }
}

const updateAllTabIndexes = (store: FocusStore) => {
  for (const [layerId] of store.elements) updateTabIndexes(store, layerId)
}

const disposeLayer = (store: FocusStore, layerId: string) => {
  const layer = store.layers.get(layerId)
  if (!layer) return

  const index = store.stack.indexOf(layerId)
  if (index !== -1) {
    store.stack.splice(index, 1)
    updateAllTabIndexes(store)
    if (layer.savedFocus?.isConnected) {
      layer.savedFocus.focus()
      if (isKeyboardInteraction) updateFocusRing(layer.savedFocus)
    }
    layer.savedFocus = null
  }

  store.elements.delete(layerId)
  store.layers.delete(layerId)
}

// --- Focus Ring ---

type FocusRingState = {
  element: HTMLElement | null
  ring: boolean | ReactElement
}

let focusRingState: FocusRingState = { element: null, ring: true }
const focusRingListeners: Set<() => void> = new Set()

const updateFocusRing = (element: HTMLElement | null, ring: boolean | ReactElement = true) => {
  focusRingState = { element, ring }
  for (const listener of focusRingListeners) listener()
}

let globalListenersInstalled = false

const installGlobalListeners = () => {
  if (globalListenersInstalled) return
  globalListenersInstalled = true

  // Track input method: keyboard vs pointer
  const handlePointerDown = () => {
    isKeyboardInteraction = false
    if (focusRingState.element) updateFocusRing(null)
  }

  document.addEventListener('mousedown', handlePointerDown, true)
  document.addEventListener('touchstart', handlePointerDown, true)

  document.addEventListener(
    'keydown',
    () => {
      isKeyboardInteraction = true
    },
    true,
  )

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target as HTMLElement
      const entry = findEntryForElement(target)
      if (isKeyboardInteraction && entry && entry.focusRing !== false) {
        updateFocusRing(entry.element, entry.focusRing)
      } else {
        updateFocusRing(null)
      }
    },
    true,
  )

  document.addEventListener(
    'focusout',
    () => {
      requestAnimationFrame(() => {
        if (!document.activeElement || document.activeElement === document.body) {
          updateFocusRing(null)
        }
      })
    },
    true,
  )

  // Key interception
  const keyToHandler: Record<
    string,
    keyof NavigationHandlers | ((event: KeyboardEvent) => keyof NavigationHandlers)
  > = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    Tab: (event) => (event.shiftKey ? 'shiftTab' : 'tab'),
    Home: 'home',
    End: 'end',
    PageUp: 'pageUp',
    PageDown: 'pageDown',
    Enter: 'select',
    ' ': 'select',
  }

  document.addEventListener(
    'keydown',
    (event) => {
      // Escape: deactivate the topmost active layer
      if (event.key === 'Escape') {
        const store = findStoreForElement(document.activeElement as HTMLElement)
        if (store) {
          const result = getTopmostActiveLayer(store)
          if (result?.layer.onEscape) {
            event.preventDefault()
            result.layer.onEscape()
          }
        }
        return
      }

      const focused = document.activeElement as HTMLElement

      const mapping = keyToHandler[event.key]
      if (!mapping) return

      const handlerKey = typeof mapping === 'function' ? mapping(event) : mapping

      // Element-level onSelect takes priority over layer handlers
      if (handlerKey === 'select') {
        const entry = findEntryForElement(focused)
        if (entry?.onSelect) {
          event.preventDefault()
          entry.onSelect()
          return
        }

        // Layer-level onEnter for Enter key specifically
        if (event.key === 'Enter') {
          const store = findStoreForElement(focused)
          if (store) {
            const result = findLayerForElement(store, focused) ?? getTopmostActiveLayer(store)
            if (result?.layer.onEnter) {
              event.preventDefault()
              result.layer.onEnter(focused)
              return
            }
          }
        }
      }

      // tab/shiftTab use topmost layer (focus trapping),
      // all other keys dispatch to the layer owning the focused element
      const store = findStoreForElement(focused)
      if (!store) return

      let target: { layerId: string; layer: Layer } | null
      if (handlerKey === 'tab' || handlerKey === 'shiftTab') {
        target = getTopmostActiveLayer(store)
      } else {
        target = findLayerForElement(store, focused)
      }
      if (!target) return

      const { layerId, layer } = target
      const entries = getElementsForLayer(store, layerId)
      if (entries.length === 0) return

      const handler = layer.handlers[handlerKey]

      if (handler) {
        event.preventDefault()
        const targets = buildTargets(focused, entries, layer.ordering)
        const setFocus = (element: HTMLElement) => {
          focusElement(element)
          updateTabIndexes(store, layerId)
        }
        handler(focused, targets, setFocus)
        return
      }

      // Fallback: Enter/Space with no select handler calls onSelect
      if (handlerKey === 'select' && layer.onSelect) {
        event.preventDefault()
        layer.onSelect(focused)
      }
    },
    false,
  )
}

// --- Provider ---

const FocusContext = createContext<FocusContextValue>({ namespace: 'default', layerId: '__root__' })

const FocusLayerProvider: FC<{
  namespace: string
  layerId: string
  children: ReactNode
}> = ({ namespace, layerId, children }) => {
  const store = getStore(namespace)
  const layer = store.layers.get(layerId)
  const active = layer?.active ?? false

  useLayoutEffect(() => {
    const layer = store.layers.get(layerId)
    if (!active || !layer) return

    if (!store.stack.includes(layerId)) store.stack.push(layerId)

    layer.savedFocus = document.activeElement as HTMLElement | null

    updateAllTabIndexes(store)

    if (isKeyboardInteraction) {
      layer.autoFocused = false
      const entries = getElementsForLayer(store, layerId)
      const autoFocusEntry = entries.find((e) => e.autoFocus && !e.disabled)
      if (autoFocusEntry) {
        layer.autoFocused = true
        focusElement(tabTarget(autoFocusEntry))
      }
    }

    return () => {
      const index = store.stack.indexOf(layerId)
      if (index !== -1) store.stack.splice(index, 1)

      updateAllTabIndexes(store)

      if (layer.savedFocus?.isConnected) {
        layer.savedFocus.focus()
        if (isKeyboardInteraction) updateFocusRing(layer.savedFocus)
      }
      layer.savedFocus = null
    }
  }, [active])

  useEffect(() => () => disposeLayer(store, layerId), [])

  return <FocusContext.Provider value={{ namespace, layerId }}>{children}</FocusContext.Provider>
}

// --- Hook ---

export function useFocus(namespace?: string): FocusAPI {
  const context = useContext(FocusContext)
  const ns = namespace ?? context.namespace
  const parentId = context.layerId
  const baseId = useId()
  const callIndexRef = useRef(0)
  const slotsRef = useRef<LayerSlot[]>([])
  const layerIdsRef = useRef<Set<string>>(new Set())

  useEffect(installGlobalListeners, [])

  useEffect(
    () => () => {
      const store = getStore(ns)
      for (const layerId of layerIdsRef.current) disposeLayer(store, layerId)
    },
    [],
  )

  callIndexRef.current = 0

  const ref = useMemo(() => createRefFn(ns, () => parentId), [ns, parentId])

  const layer = (active: boolean, options?: LayerOptions): FocusLayer => {
    const index = callIndexRef.current
    callIndexRef.current += 1
    const layerId = `${baseId}:${options?.name ?? index}`
    layerIdsRef.current.add(layerId)
    return createLayerFromSlots(ns, slotsRef.current, index, layerId, active, options, parentId)
  }

  const ordering = (keys: string[]) => {
    const store = getStore(ns)
    const layer = store.layers.get(parentId)
    if (layer) layer.ordering = keys
  }

  const dispose = () => {}

  return useMemo(() => ({ ref, layer, ordering, dispose }), [ref])
}

function normalizeRefOptions(options?: RefOptions | RefObject<HTMLElement | null>): RefOptions {
  if (!options) return {}
  if ('current' in options && !('disabled' in options) && !('focusRing' in options))
    return { ref: options as RefObject<HTMLElement | null> }
  return options as RefOptions
}

function createRefFn(
  namespace: string,
  getLayerId: () => string,
): (options?: RefOptions | RefObject<HTMLElement | null>) => (el: HTMLElement | null) => void {
  return (options) => {
    const opts = normalizeRefOptions(options)
    let prevEl: HTMLElement | null = null

    return (el: HTMLElement | null) => {
      if (opts.ref) (opts.ref as any).current = el

      if (opts.focusable === false) return

      const layerId = getLayerId()
      const store = getStore(namespace)
      const elements = getLayerElements(store, layerId)

      if (el) {
        if (prevEl && prevEl !== el) elements.delete(prevEl)
        prevEl = el

        const isDisabled = opts.disabled ?? ('disabled' in el ? (el as any).disabled : false)
        const target = normalizeToEl(opts.focusTarget) ?? undefined

        const entry: ElementEntry = {
          element: el,
          target,
          disabled: isDisabled,
          autoFocus: opts.autoFocus ?? false,
          focusRing: opts.focusRing ?? true,
          onSelect: opts.onSelect,
          ordering: opts.ordering,
        }
        elements.set(el, entry)

        updateTabIndexes(store, layerId)
      } else if (prevEl) {
        elements.delete(prevEl)
        prevEl = null
      }
    }
  }
}

function createLayerFromSlots(
  namespace: string,
  slots: LayerSlot[],
  index: number,
  layerId: string,
  active: boolean,
  options: LayerOptions | undefined,
  parentId: string,
): FocusLayer {
  let slot = slots[index]
  if (!slot || slot.layerId !== layerId) {
    const capturedNs = namespace
    const capturedId = layerId
    const provider: FC<{ children: ReactNode }> = ({ children }) => (
      <FocusLayerProvider namespace={capturedNs} layerId={capturedId}>
        {children}
      </FocusLayerProvider>
    )
    slot = { provider, layerId, subSlots: slot?.subSlots ?? [] }
    slots[index] = slot
  }

  const store = getStore(namespace)
  const { handlers, roving } = resolveNavigation(options?.navigation)
  const existing = store.layers.get(layerId)

  if (existing) {
    existing.handlers = handlers
    existing.roving = roving
    existing.active = active
    existing.onSelect = options?.onSelect
    existing.onEnter = options?.onEnter
    existing.onEscape = options?.onEscape
    existing.parentId = parentId
    existing.ordering = existing.ordering ?? null
  } else {
    store.layers.set(layerId, {
      id: layerId,
      handlers,
      roving,
      active,
      autoFocused: false,
      onSelect: options?.onSelect,
      onEnter: options?.onEnter,
      onEscape: options?.onEscape,
      savedFocus: null,
      parentId,
      ordering: null,
    })
  }

  const layerApi = createLayerApi(namespace, layerId, slot.subSlots)
  return [slot.provider, layerApi] as const
}

function createLayerApi(namespace: string, layerId: string, slots: LayerSlot[]): FocusAPI {
  let callIndex = 0

  const ref = createRefFn(namespace, () => layerId)

  const layer = (active: boolean, options?: LayerOptions): FocusLayer => {
    const index = callIndex
    callIndex += 1
    const subLayerId = `${layerId}:${options?.name ?? index}`
    return createLayerFromSlots(namespace, slots, index, subLayerId, active, options, layerId)
  }

  const ordering = (keys: string[]) => {
    const store = getStore(namespace)
    const layer = store.layers.get(layerId)
    if (layer) layer.ordering = keys
  }

  const dispose = () => disposeLayer(getStore(namespace), layerId)

  return { ref, layer, ordering, dispose }
}

// --- Focus Ring Overlay ---

export const FocusRingOverlay: FC = () => {
  const [state, setState] = useState(focusRingState)

  useEffect(() => {
    const listener = () => setState({ ...focusRingState })
    focusRingListeners.add(listener)
    return () => {
      focusRingListeners.delete(listener)
    }
  }, [])

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(({ rects }) => ({
        mainAxis: -rects.reference.height,
      })),
      size({
        apply({ rects, elements }) {
          elements.floating.style.width = `${rects.reference.width}px`
          elements.floating.style.height = `${rects.reference.height}px`
        },
      }),
    ],
  })

  useLayoutEffect(() => {
    if (state.element && state.ring !== false) {
      refs.setReference(state.element)
    } else {
      refs.setReference(null)
    }
  }, [state.element, state.ring])

  if (!state.element || state.ring === false) return null

  const ringContent = state.ring === true ? <FocusRing /> : state.ring

  return (
    <div ref={refs.setFloating} style={{ ...floatingStyles, pointerEvents: 'none', zIndex: 99999 }}>
      {ringContent}
    </div>
  )
}
